/**
 * Teens Bundle Builder
 * Reads config from window.TBB_CONFIG (set by bundle-builder-teens-boys.liquid
 * or bundle-builder-teens-girls.liquid — whichever is on the current page).
 *
 * Shared across both gender pages. The root element's data-default-gender
 * attribute tells this script which gender it's running for, so the same
 * file works correctly on both /pages/teens-bundle-boys and
 * /pages/teens-bundle-girls without any hardcoded gender.
 *
 * Responsibilities:
 *  - Fetch products from each configured per-category collection
 *  - Category picks exactly ONE collection to display (not merged)
 *  - Manage the bundle (add/remove/reorder), gift note, gift wrap, checkout
 */
(function () {
  'use strict';

  var CONFIG = window.TBB_CONFIG || {};
  var root = document.getElementById('bundle-builder');
  if (!root) return;

  // ---------- State ----------
  // Default gender comes from the page itself (data-default-gender),
  // not a hardcoded value — this is what lets one JS file serve both
  // the boys page and the girls page correctly.
  var defaultGender = root.getAttribute('data-default-gender') || 'boys';
  var defaultCategories = (CONFIG.genderCategories || {})[defaultGender] || [];

  var state = {
    allProducts: [],       // every fetched product, each tagged with its source category key(s)
    gender: defaultGender, // fixed per-page — no gender switcher on these pages
    category: defaultCategories[0] || null, // the single active category key
    sort: 'recommended',
    budget: CONFIG.defaultBudget || 6000,
    bundle: [],            // [{ id, variantId, title, image, price, handle }]
    selectedVariants: {},  // { productId: variantId } — customer's chosen variant, e.g. color/size
    giftWrap: true,
    giftNote: '',
    loaded: false
  };

  // ---------- Element refs ----------
  var el = {
    grid: document.getElementById('bb-products-grid'),
    carouselPrev: document.getElementById('bb-carousel-prev'),
    carouselNext: document.getElementById('bb-carousel-next'),
    loadMore: document.getElementById('bb-load-more'),
    sort: document.getElementById('bb-sort'),
    categoryRow: document.getElementById('bb-category-filter'),
    budgetDisplay: document.getElementById('bb-budget-display'),
    budgetChangeBtn: document.getElementById('bb-budget-change-btn'),
    budgetSliderWrap: document.getElementById('bb-budget-slider-wrap'),
    budgetSlider: document.getElementById('bb-budget-slider'),
    budgetConfirmBtn: document.getElementById('bb-budget-confirm-btn'),
    itemCount: document.getElementById('bb-item-count'),
    totalDisplay: document.getElementById('bb-total-display'),
    remainingDisplay: document.getElementById('bb-remaining-display'),
    progressFill: document.getElementById('bb-progress-fill'),
    budgetLabel: document.getElementById('bb-budget-label'),
    tip: document.getElementById('bb-tip'),
    bundleGrid: document.getElementById('bb-bundle-grid'),
    bundleEmpty: document.getElementById('bb-bundle-empty'),
    footerCount: document.getElementById('bb-footer-count'),
    footerTotal: document.getElementById('bb-footer-total'),
    footerRemaining: document.getElementById('bb-footer-remaining'),
    giftNote: document.getElementById('bb-gift-note'),
    noteCount: document.getElementById('bb-note-count'),
    addToCartBtn: document.getElementById('bb-add-to-cart'),
    saveBundleBtn: document.getElementById('bb-save-bundle'),
    toast: document.getElementById('bb-toast'),
    modalOverlay: document.getElementById('bb-modal-overlay'),
    modalClose: document.getElementById('bb-modal-close'),
    modalLink: document.getElementById('bb-modal-link'),
    modalCopy: document.getElementById('bb-modal-copy'),
    mobileBar: document.getElementById('bb-mobile-bar'),
    mobileCount: document.getElementById('bb-mobile-count'),
    mobileItemsLabel: document.getElementById('bb-mobile-items-label'),
    mobileRemaining: document.getElementById('bb-mobile-remaining'),
    mobileViewBundleBtn: document.getElementById('bb-mobile-view-bundle')
  };

  // ---------- Helpers ----------
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(0);
    if (CONFIG.moneyFormat && CONFIG.moneyFormat.indexOf('{{amount}}') > -1) {
      return CONFIG.moneyFormat.replace('{{amount}}', amount);
    }
    return (CONFIG.currencySymbol || '₹') + amount;
  }

  function showToast(message) {
    if (!el.toast) return;
    el.toast.textContent = message;
    el.toast.classList.add('bb-toast--visible');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(function () {
      el.toast.classList.remove('bb-toast--visible');
    }, 2500);
  }

  // ---------- Fetch collections ----------
  function fetchCollection(handle) {
    if (!handle) return Promise.resolve([]);
    return fetch('/collections/' + handle + '/products.json?limit=250')
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to fetch collection: ' + handle);
        return res.json();
      })
      .then(function (data) {
        return data.products || [];
      })
      .catch(function (err) {
        console.error('[Teens Bundle Builder]', err);
        return [];
      });
  }

  function loadAllProducts() {
    var collections = CONFIG.collections || {};
    var keys = Object.keys(collections);

    return Promise.all(
      keys.map(function (key) {
        return fetchCollection(collections[key]).then(function (products) {
          return { key: key, products: products };
        });
      })
    ).then(function (results) {
      var byId = {};

      results.forEach(function (result) {
        result.products.forEach(function (product) {
          if (!byId[product.id]) {
            var variants = (product.variants || []).map(function (v) {
              return {
                id: v.id,
                title: v.title,
                price: Math.round(parseFloat(v.price) * 100),
                available: v.available,
                image: v.featured_image ? v.featured_image.src : null
              };
            });
            var firstAvailable = variants.filter(function (v) { return v.available; })[0] || variants[0];
            var hasRealOptions = variants.length > 1 && variants.some(function (v) { return v.title !== 'Default Title'; });

            byId[product.id] = {
              id: product.id,
              variantId: firstAvailable ? firstAvailable.id : null,
              title: product.title,
              handle: product.handle,
              price: firstAvailable ? firstAvailable.price : 0,
              image: product.images && product.images[0] ? product.images[0].src : '',
              available: firstAvailable ? firstAvailable.available : false,
              variants: variants,
              hasVariants: hasRealOptions,
              categories: []
            };
          }
          if (byId[product.id].categories.indexOf(result.key) === -1) {
            byId[product.id].categories.push(result.key);
          }
        });
      });

      state.allProducts = Object.keys(byId).map(function (id) {
        return byId[id];
      });
      state.loaded = true;
    });
  }

  // ---------- Filtering ----------
  // Only the single active category's collection is shown — never merged
  // with siblings, and never all categories at once.
  function getVisibleProducts() {
    var activeKeys = [state.category];
    var budgetCents = state.budget * 100;

    var products = state.allProducts.filter(function (product) {
      var inCategory = product.categories.some(function (cat) {
        return activeKeys.indexOf(cat) > -1;
      });
      return inCategory && product.price <= budgetCents;
    });

    if (state.sort === 'price-asc') {
      products = products.slice().sort(function (a, b) { return a.price - b.price; });
    } else if (state.sort === 'price-desc') {
      products = products.slice().sort(function (a, b) { return b.price - a.price; });
    }

    return products;
  }

  // ---------- Rendering ----------
  function renderGrid() {
    if (!el.grid) return;

    if (!state.loaded) {
      el.grid.innerHTML = skeletonMarkup(4);
      return;
    }

    var products = getVisibleProducts();

    if (!products.length) {
      el.grid.innerHTML = '<p class="bb-empty-message">No products found in this category within your budget.</p>';
      if (el.loadMore) el.loadMore.style.display = 'none';
      return;
    }

    el.grid.innerHTML = products.map(productCardMarkup).join('');
    if (el.loadMore) el.loadMore.style.display = 'none';

    el.grid.querySelectorAll('[data-add-product]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-add-product');
        addToBundle(id);
      });
    });

    el.grid.querySelectorAll('[data-variant-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var productId = select.getAttribute('data-variant-select');
        state.selectedVariants[productId] = select.value;

        var product = state.allProducts.filter(function (p) { return String(p.id) === String(productId); })[0];
        var variant = product && product.variants.filter(function (v) { return String(v.id) === String(select.value); })[0];
        if (variant) {
          var card = select.closest('.bb-product-card');

          var priceEl = card.querySelector('.bb-product-card__price');
          if (priceEl) priceEl.textContent = formatMoney(variant.price);

          var imageEl = card.querySelector('.bb-product-card__image');
          var newImageSrc = variant.image || product.image;
          if (imageEl && newImageSrc) {
            imageEl.src = newImageSrc;
          }
        }
      });
    });
  }

  function skeletonMarkup(count) {
    var out = '';
    for (var i = 0; i < count; i++) out += '<div class="bb-product-skeleton"></div>';
    return out;
  }

  function productCardMarkup(product) {
    var inBundle = state.bundle.some(function (item) { return item.id === product.id; });
    var disabled = !product.available || inBundle;
    var label = !product.available ? 'Sold Out' : (inBundle ? 'Added' : 'Add');
    var productUrl = (CONFIG.shopUrl || '') + '/products/' + product.handle;
    var selectedVariantId = state.selectedVariants[product.id] || product.variantId;
    var selectedVariant = product.variants.filter(function (v) { return String(v.id) === String(selectedVariantId); })[0];
    var initialImage = (selectedVariant && selectedVariant.image) || product.image;

    var variantPicker = '';
    if (product.hasVariants) {
      variantPicker =
        '<select class="bb-product-card__variant" data-variant-select="' + product.id + '"' + (inBundle ? ' disabled' : '') + '>' +
          product.variants.map(function (v) {
            var isSelected = String(v.id) === String(selectedVariantId);
            return '<option value="' + v.id + '"' + (isSelected ? ' selected' : '') + (!v.available ? ' disabled' : '') + '>' +
              escapeHtml(v.title) + (!v.available ? ' (Sold Out)' : '') +
            '</option>';
          }).join('') +
        '</select>';
    }

    return (
      '<div class="bb-product-card">' +
        '<a class="bb-product-card__link" href="' + productUrl + '" target="_blank" rel="noopener">' +
          '<div class="bb-product-card__image-wrap">' +
            (initialImage ? '<img class="bb-product-card__image" src="' + initialImage + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="bb-product-card__body">' +
            '<h4 class="bb-product-card__title">' + escapeHtml(product.title) + '</h4>' +
            '<div class="bb-product-card__price">' + formatMoney(product.price) + '</div>' +
            '<span class="bb-product-card__learn-more">Learn more →</span>' +
          '</div>' +
        '</a>' +
        variantPicker +
        '<button class="bb-product-card__add" data-add-product="' + product.id + '"' + (disabled ? ' disabled' : '') + '>' + label + '</button>' +
      '</div>'
    );
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // ---------- Bundle management ----------
  function bundleTotal() {
    var itemsTotal = state.bundle.reduce(function (sum, item) { return sum + item.price; }, 0);
    var wrapCost = (state.giftWrap && CONFIG.giftWrapPrice) ? CONFIG.giftWrapPrice * 100 : 0;
    return itemsTotal + wrapCost;
  }

  function addToBundle(productId) {
    var product = state.allProducts.filter(function (p) { return String(p.id) === String(productId); })[0];
    if (!product) return;

    var chosenVariantId = state.selectedVariants[productId] || product.variantId;
    var chosenVariant = product.variants.filter(function (v) { return String(v.id) === String(chosenVariantId); })[0];
    var itemPrice = chosenVariant ? chosenVariant.price : product.price;
    var itemVariantTitle = chosenVariant && chosenVariant.title !== 'Default Title' ? chosenVariant.title : '';

    var remaining = state.budget * 100 - bundleTotal();
    if (itemPrice > remaining) {
      showToast('Adding this would go over your budget.');
      return;
    }

    state.bundle.push({
      id: product.id,
      variantId: chosenVariantId,
      title: product.title + (itemVariantTitle ? ' (' + itemVariantTitle + ')' : ''),
      handle: product.handle,
      image: product.image,
      price: itemPrice
    });
    renderGrid();
    renderBundle();
    showToast(product.title + ' added to your bundle');
  }

  function removeFromBundle(productId) {
    state.bundle = state.bundle.filter(function (p) { return String(p.id) !== String(productId); });
    renderGrid();
    renderBundle();
  }

  function renderBundle() {
    var count = state.bundle.length;
    var total = bundleTotal();
    var remaining = Math.max(state.budget * 100 - total, 0);
    var pct = state.budget > 0 ? Math.min((total / (state.budget * 100)) * 100, 100) : 0;

    if (el.itemCount) el.itemCount.textContent = count;
    if (el.totalDisplay) el.totalDisplay.textContent = formatMoney(total);
    if (el.remainingDisplay) el.remainingDisplay.textContent = formatMoney(remaining);
    if (el.progressFill) el.progressFill.style.width = pct + '%';
    if (el.footerCount) el.footerCount.textContent = count;
    if (el.footerTotal) el.footerTotal.textContent = formatMoney(total);
    if (el.footerRemaining) el.footerRemaining.textContent = formatMoney(remaining);
    if (el.addToCartBtn) el.addToCartBtn.disabled = count === 0;

    if (el.mobileCount) el.mobileCount.textContent = count;
    if (el.mobileItemsLabel) el.mobileItemsLabel.textContent = count + (count === 1 ? ' Item' : ' Items') + ' in your bundle';
    if (el.mobileRemaining) el.mobileRemaining.textContent = (remaining / 100).toFixed(0);

    if (!el.bundleGrid) return;

    if (count === 0) {
      el.bundleGrid.innerHTML =
        '<div class="bb-bundle-empty" id="bb-bundle-empty">' +
          '<span class="bb-bundle-empty__icon">🎁</span>' +
          '<p>Your bundle is empty. Add items from Recommendations above.</p>' +
        '</div>';
      return;
    }

    el.bundleGrid.innerHTML = state.bundle.map(function (item) {
      return (
        '<div class="bb-bundle-item">' +
          (item.image ? '<img class="bb-bundle-item__image" src="' + item.image + '" alt="' + escapeHtml(item.title) + '">' : '') +
          '<div class="bb-bundle-item__title">' + escapeHtml(item.title) + '</div>' +
          '<div class="bb-bundle-item__price">' + formatMoney(item.price) + '</div>' +
          '<button class="bb-bundle-item__remove" data-remove-product="' + item.id + '" aria-label="Remove">✕</button>' +
        '</div>'
      );
    }).join('');

    el.bundleGrid.querySelectorAll('[data-remove-product]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeFromBundle(btn.getAttribute('data-remove-product'));
      });
    });
  }

  // ---------- Event wiring ----------

  // Renders the category buttons for this page's fixed gender
  // (4 for boys, 5 for girls, depending on which page loaded this script).
  function renderCategoryButtons() {
    if (!el.categoryRow) return;
    var categories = (CONFIG.genderCategories || {})[state.gender] || [];
    var labels = CONFIG.categoryLabels || {};

    el.categoryRow.innerHTML = categories.map(function (key) {
      var isActive = key === state.category;
      return (
        '<button class="bb-chip' + (isActive ? ' bb-chip--active' : '') + '" data-category="' + key + '">' +
          escapeHtml(labels[key] || key) +
        '</button>'
      );
    }).join('');

    wireCategoryButtons();
  }

  // Clicking a category button sets state.category to that ONE key —
  // getVisibleProducts() then shows only that single collection's products.
  function wireCategoryButtons() {
    if (!el.categoryRow) return;
    el.categoryRow.querySelectorAll('[data-category]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        el.categoryRow.querySelectorAll('.bb-chip').forEach(function (c) {
          c.classList.remove('bb-chip--active');
        });
        chip.classList.add('bb-chip--active');

        state.category = chip.getAttribute('data-category');
        renderGrid();
      });
    });
  }

  function wireCarousel() {
    if (!el.grid) return;

    function scrollByCards(direction) {
      var card = el.grid.querySelector('.bb-product-card, .bb-product-skeleton');
      var cardWidth = card ? card.getBoundingClientRect().width + 12 : 160;
      el.grid.scrollBy({ left: direction * cardWidth * 4, behavior: 'smooth' });
    }

    if (el.carouselPrev) {
      el.carouselPrev.addEventListener('click', function () { scrollByCards(-1); });
    }
    if (el.carouselNext) {
      el.carouselNext.addEventListener('click', function () { scrollByCards(1); });
    }
  }

  function wireSort() {
    if (!el.sort) return;
    el.sort.addEventListener('change', function () {
      state.sort = el.sort.value;
      renderGrid();
    });
  }

  function wireBudget() {
    if (el.budgetChangeBtn) {
      el.budgetChangeBtn.addEventListener('click', function () {
        el.budgetSliderWrap.style.display = 'block';
      });
    }
    if (el.budgetSlider) {
      el.budgetSlider.addEventListener('input', function () {
        state.budget = parseInt(el.budgetSlider.value, 10);
        if (el.budgetDisplay) el.budgetDisplay.textContent = state.budget;
        if (el.budgetLabel) el.budgetLabel.textContent = state.budget;
        renderBundle();
        renderGrid();
      });
    }
    if (el.budgetConfirmBtn) {
      el.budgetConfirmBtn.addEventListener('click', function () {
        el.budgetSliderWrap.style.display = 'none';
      });
    }
  }

  function wireGiftNote() {
    if (!el.giftNote) return;
    el.giftNote.addEventListener('input', function () {
      state.giftNote = el.giftNote.value;
      if (el.noteCount) el.noteCount.textContent = el.giftNote.value.length;
    });
  }

  function wireGiftWrap() {
    root.querySelectorAll('input[name="gift-wrap"]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.giftWrap = input.value === 'yes';
        renderBundle();
      });
    });
  }

  function wireAddToCart() {
    if (!el.addToCartBtn) return;
    el.addToCartBtn.addEventListener('click', function () {
      if (!state.bundle.length) return;

      var items = state.bundle
        .filter(function (item) { return item.variantId; })
        .map(function (item) {
          return {
            id: item.variantId,
            quantity: 1,
            properties: {
              'Bundle Note': state.giftNote || '',
              'Gift Wrap': state.giftWrap ? 'Yes' : 'No'
            }
          };
        });

      if (state.giftWrap && CONFIG.giftWrapProductId) {
        items.push({ id: CONFIG.giftWrapProductId, quantity: 1 });
      }

      if (!items.length) return;

      el.addToCartBtn.disabled = true;
      el.addToCartBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed');
          return res.json();
        })
        .then(function () {
          showToast('Bundle added to cart!');
          window.location.href = '/cart';
        })
        .catch(function (err) {
          console.error('[Teens Bundle Builder]', err);
          showToast('Something went wrong adding to cart.');
        })
        .finally(function () {
          el.addToCartBtn.disabled = state.bundle.length === 0;
          el.addToCartBtn.textContent = '🛒 Add Bundle to Cart';
        });
    });
  }

  function wireSaveBundle() {
    if (!el.saveBundleBtn) return;
    el.saveBundleBtn.addEventListener('click', function () {
      if (!state.bundle.length) {
        showToast('Add items to your bundle first.');
        return;
      }
      var handles = state.bundle.map(function (item) { return item.handle; }).join(',');
      var link = (CONFIG.shopUrl || '') + (CONFIG.pageUrl || '') + '?bundle=' + encodeURIComponent(handles) + '&budget=' + state.budget;

      if (el.modalLink) el.modalLink.value = link;
      if (el.modalOverlay) el.modalOverlay.style.display = 'flex';
    });

    if (el.modalClose) {
      el.modalClose.addEventListener('click', function () {
        el.modalOverlay.style.display = 'none';
      });
    }
    if (el.modalCopy) {
      el.modalCopy.addEventListener('click', function () {
        if (!el.modalLink) return;
        el.modalLink.select();
        navigator.clipboard.writeText(el.modalLink.value).then(function () {
          showToast('Link copied!');
        });
      });
    }
  }

  function wireMobileBar() {
    if (!el.mobileViewBundleBtn) return;
    el.mobileViewBundleBtn.addEventListener('click', function () {
      var target = document.querySelector('.bb-bundle-section');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ---------- Restore a saved bundle from the URL, if present ----------
  function restoreFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var budgetParam = parseInt(params.get('budget'), 10);
    if (!isNaN(budgetParam)) {
      state.budget = budgetParam;
      if (el.budgetDisplay) el.budgetDisplay.textContent = state.budget;
      if (el.budgetLabel) el.budgetLabel.textContent = state.budget;
      if (el.budgetSlider) el.budgetSlider.value = state.budget;
    }

    var bundleParam = params.get('bundle');
    if (!bundleParam) return;
    var handles = bundleParam.split(',');
    state.bundle = state.allProducts.filter(function (p) {
      return handles.indexOf(p.handle) > -1;
    });
    renderBundle();
  }

  function syncGiftWrapFromDOM() {
    var checkedInput = root.querySelector('input[name="gift-wrap"]:checked');
    state.giftWrap = checkedInput ? checkedInput.value === 'yes' : true;
  }

  // ---------- Init ----------
  function init() {
    syncGiftWrapFromDOM();
    renderCategoryButtons();
    renderGrid();
    wireCarousel();
    wireSort();
    wireBudget();
    wireGiftNote();
    wireGiftWrap();
    wireAddToCart();
    wireSaveBundle();
    wireMobileBar();

    loadAllProducts().then(function () {
      renderGrid();
      restoreFromUrl();
      renderBundle();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();